import {
  blank,
  box,
  lines,
  separator,
  table,
  type Column,
  type LayoutFn,
  type Line,
  type Theme,
} from "../termui";
import { dark } from "../theme";
import {
  headingColor,
  isNumericValue,
  renderPrefixedText,
  wrapText,
} from "./common";
import type { MarkdownThemeOverrides } from "./types";

type MarkdownBlock =
  | { type: "heading"; level: number; text: string }
  | { type: "paragraph"; text: string }
  | { type: "unordered-list"; items: string[] }
  | { type: "ordered-list"; start: number; items: string[] }
  | { type: "table"; headers: string[]; rows: string[][] }
  | { type: "code"; language?: string; lines: string[] }
  | { type: "quote"; lines: string[] }
  | { type: "separator" };

function isBlockStart(line: string): boolean {
  const trimmed = line.trim();
  if (!trimmed) return false;
  if (/^#{1,6}\s+/.test(trimmed)) return true;
  if (/^```/.test(trimmed)) return true;
  if (/^>\s?/.test(trimmed)) return true;
  if (/^[-*+]\s+/.test(trimmed)) return true;
  if (/^\d+\.\s+/.test(trimmed)) return true;
  const collapsed = trimmed.replace(/\s+/g, "");
  return /^(-{3,}|\*{3,}|_{3,})$/.test(collapsed);
}

function parsePipeCells(line: string): string[] | null {
  const trimmed = line.trim();
  if (!trimmed.includes("|")) return null;

  let core = trimmed;
  if (core.startsWith("|")) core = core.slice(1);
  if (core.endsWith("|")) core = core.slice(0, -1);
  const cells = core.split("|").map((cell) => cell.trim());
  return cells.length >= 2 ? cells : null;
}

function isTableDividerLine(line: string): boolean {
  const cells = parsePipeCells(line);
  if (!cells) return false;
  return cells.every((cell) => /^:?-{3,}:?$/.test(cell));
}

function parseMarkdown(markdown: string): MarkdownBlock[] {
  const rows = markdown.replace(/\r\n/g, "\n").split("\n");
  const blocks: MarkdownBlock[] = [];

  let i = 0;
  while (i < rows.length) {
    const raw = rows[i] ?? "";
    const trimmed = raw.trim();

    if (!trimmed) {
      i += 1;
      continue;
    }

    const codeStart = trimmed.match(/^```([\w+-]+)?\s*$/);
    if (codeStart) {
      i += 1;
      const codeLines: string[] = [];
      while (i < rows.length && !(rows[i] ?? "").trim().startsWith("```")) {
        codeLines.push((rows[i] ?? "").replace(/\t/g, "  "));
        i += 1;
      }
      if (i < rows.length) i += 1;
      blocks.push({
        type: "code",
        language: codeStart[1],
        lines: codeLines,
      });
      continue;
    }

    const heading = trimmed.match(/^(#{1,6})\s+(.*)$/);
    if (heading) {
      blocks.push({
        type: "heading",
        level: heading[1]!.length,
        text: heading[2]!.trim(),
      });
      i += 1;
      continue;
    }

    const collapsed = trimmed.replace(/\s+/g, "");
    if (/^(-{3,}|\*{3,}|_{3,})$/.test(collapsed)) {
      blocks.push({ type: "separator" });
      i += 1;
      continue;
    }

    const maybeHeaderCells = parsePipeCells(raw);
    const nextLine = rows[i + 1] ?? "";
    if (maybeHeaderCells && isTableDividerLine(nextLine)) {
      const headers = maybeHeaderCells;
      i += 2; // consume header + divider
      const dataRows: string[][] = [];
      while (i < rows.length) {
        const rowCells = parsePipeCells(rows[i] ?? "");
        if (!rowCells) break;
        const normalized = headers.map((_, idx) => rowCells[idx] ?? "");
        dataRows.push(normalized);
        i += 1;
      }
      blocks.push({ type: "table", headers, rows: dataRows });
      continue;
    }

    if (/^>\s?/.test(trimmed)) {
      const quoteLines: string[] = [];
      while (i < rows.length) {
        const m = (rows[i] ?? "").match(/^\s*>\s?(.*)$/);
        if (!m) break;
        quoteLines.push(m[1]!);
        i += 1;
      }
      blocks.push({ type: "quote", lines: quoteLines });
      continue;
    }

    const ulStart = trimmed.match(/^[-*+]\s+(.*)$/);
    if (ulStart) {
      const items: string[] = [];
      while (i < rows.length) {
        const current = (rows[i] ?? "").trim();
        const item = current.match(/^[-*+]\s+(.*)$/);
        if (item) {
          items.push(item[1]!.trim());
          i += 1;
          continue;
        }
        const continuation = (rows[i] ?? "").match(/^\s{2,}(.*)$/);
        if (continuation && items.length > 0) {
          const last = items[items.length - 1] ?? "";
          items[items.length - 1] = (last + " " + continuation[1]!.trim()).trim();
          i += 1;
          continue;
        }
        break;
      }
      blocks.push({ type: "unordered-list", items });
      continue;
    }

    const olStart = trimmed.match(/^(\d+)\.\s+(.*)$/);
    if (olStart) {
      const items: string[] = [];
      const start = Number(olStart[1]);
      while (i < rows.length) {
        const current = (rows[i] ?? "").trim();
        const item = current.match(/^\d+\.\s+(.*)$/);
        if (item) {
          items.push(item[1]!.trim());
          i += 1;
          continue;
        }
        const continuation = (rows[i] ?? "").match(/^\s{2,}(.*)$/);
        if (continuation && items.length > 0) {
          const last = items[items.length - 1] ?? "";
          items[items.length - 1] = (last + " " + continuation[1]!.trim()).trim();
          i += 1;
          continue;
        }
        break;
      }
      blocks.push({ type: "ordered-list", start, items });
      continue;
    }

    const paragraphRows: string[] = [trimmed];
    i += 1;
    while (i < rows.length) {
      const nextRaw = rows[i] ?? "";
      const next = (rows[i] ?? "").trim();
      const maybeTableStart =
        parsePipeCells(nextRaw) !== null && isTableDividerLine(rows[i + 1] ?? "");
      if (!next || isBlockStart(next) || maybeTableStart) break;
      paragraphRows.push(next);
      i += 1;
    }
    blocks.push({ type: "paragraph", text: paragraphRows.join(" ") });
  }

  return blocks;
}

function renderMarkdownBlock(
  block: MarkdownBlock,
  theme: Theme,
  overrides?: MarkdownThemeOverrides,
): LayoutFn {
  const md = theme.markdown;

  switch (block.type) {
    case "heading":
      return (w) =>
        renderPrefixedText(
          block.text,
          w,
          {
            text: "#".repeat(block.level) + " ",
            color: overrides?.headingMarker ?? md.headingMarker,
          },
          " ".repeat(block.level + 1),
          {
            color: headingColor(block.level, theme, overrides),
            bold: block.level <= (overrides?.headingBoldMaxLevel ?? md.headingBoldMaxLevel),
          },
        );

    case "paragraph":
      return (w) =>
        wrapText(block.text, w).map((line) => [
          { text: line, color: overrides?.paragraph ?? md.paragraph },
        ]);

    case "unordered-list":
      return (w) => {
        const out: Line[] = [];
        for (const item of block.items) {
          out.push(
            ...renderPrefixedText(
              item,
              w,
              { text: " • ", color: overrides?.unorderedBullet ?? md.unorderedBullet },
              "   ",
            ),
          );
        }
        return out;
      };

    case "ordered-list":
      return (w) => {
        const out: Line[] = [];
        const lastNumber = block.start + block.items.length - 1;
        const digits = String(Math.max(block.start, lastNumber)).length;
        for (let i = 0; i < block.items.length; i++) {
          const n = String(block.start + i).padStart(digits, " ");
          const prefix = {
            text: `${n}. `,
            color: overrides?.orderedMarker ?? md.orderedMarker,
          };
          out.push(...renderPrefixedText(block.items[i]!, w, prefix, " ".repeat(digits + 2)));
        }
        return out;
      };

    case "table": {
      const keys = block.headers.map((_, i) => `c${i}`);
      const columns: Column[] = block.headers.map((header, i) => ({
        key: keys[i]!,
        title: header || `col_${i + 1}`,
        align: block.rows.every((row) => isNumericValue(row[i] ?? "")) ? "right" : "left",
        headerColor: overrides?.tableHeader ?? md.tableHeader,
      }));

      const data = block.rows.map((row) => {
        const out: Record<string, string> = {};
        keys.forEach((key, i) => {
          out[key] = row[i] ?? "";
        });
        return out;
      });

      return table(columns, data, {
        borderColor: overrides?.tableBorder ?? md.tableBorder,
      });
    }

    case "quote":
      return (w) => {
        const out: Line[] = [];
        for (const quoteLine of block.lines) {
          out.push(
            ...renderPrefixedText(
              quoteLine,
              w,
              { text: "▌ ", color: overrides?.quoteMarker ?? md.quoteMarker },
              "  ",
              { color: overrides?.quoteText ?? md.quoteText, dim: true },
            ),
          );
        }
        return out;
      };

    case "separator":
      return separator(overrides?.separator ?? md.separator);

    case "code": {
      const codeSource = block.lines.join("\n");
      return box(
        lines(
          block.lines.length > 0
            ? block.lines.map((line) => [{ text: line || " ", color: overrides?.codeText ?? md.codeText }])
            : [[{ text: " ", color: overrides?.codeEmpty ?? md.codeEmpty, dim: true }]],
        ),
        {
          title: block.language ? `code:${block.language}` : "code",
          borderColor: overrides?.codeBorder ?? md.codeBorder,
          action: {
            label: "cp",
            onClick: () => navigator.clipboard.writeText(codeSource),
            color: theme.semantic.frameMuted,
          },
        },
      );
    }
  }
}

export function fromMarkdown(
  markdown: string,
  opts?: { theme?: Theme; overrides?: MarkdownThemeOverrides },
): LayoutFn {
  const theme = opts?.theme ?? dark;
  const blocks = parseMarkdown(markdown);
  const layouts = blocks.map((block) => renderMarkdownBlock(block, theme, opts?.overrides));

  return (w) => {
    if (layouts.length === 0) return blank()(w);

    const out: Line[] = [];
    layouts.forEach((layout, index) => {
      if (index > 0) out.push(...blank()(w));
      out.push(...layout(w));
    });
    return out;
  };
}
